import React, { useEffect, Fragment, useContext, useState } from "react";
import { httpGet } from "../lib/apiClients";
import {
  Box,
  Container,
  Select,
  Typography,
  MenuItem,
  FormControl,
  Grid,
  Button,
} from "@material-ui/core";
import { ThemeContext } from "../context/ThemeContext";
import { AuthContext } from "../auth/AuthContext";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import { AxiosResponse } from "axios";
import { dateTimeToUnix, formatLocaleDate } from "../utils/dateUtils";
import { formatDate } from "../utils/dateUtils";
import LoaderComponent from "../components/LoaderComponent";
import ButtonLoader from "../components/ButtonLoader";
import {
  hasExpired,
  minutesLeftTillSupport,
  printDateTime,
} from "../lib/dateCalculate";
import SectionHeader from "../components/SectionHeader";
import { Environment } from "../utils/environments";
export default function PurchaseListPage() {
  const { getPurchaseList } = useContext(ThemeContext);
  const [purchaseList, setPurchaseList] = getPurchaseList;
  const { currentUser } = useContext(AuthContext);
  const [period, setPeriod] = useState("過去3ヶ月");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "お支払い履歴 | じぶん専用IT相談室";
    if (currentUser === undefined) {
      return;
    }
    if (!currentUser) {
      navigate("/login", { state: { from: window.location.pathname } });
      return;
    } else {
      if (!purchaseList) {
        getPurchaseListData("");
      }
    }
  }, [currentUser]);
  /**
   * Fetches purchase list data for a specific period.
   *
   * @param {string} period - The period for which to retrieve the purchase list data.
   * @returns {Promise<void>} A promise that resolves when the purchase list data is fetched and transformed.
   */
  const getPurchaseListData = async (period) => {
    setPurchaseList(undefined);
    try {
      await httpGet({
        pathName: "purchase-list",
        queryParams: {
          period: period,
          memberId: "",
          ticketNumber: "",
          purchaseTime: "",
        },
      }).then((response: AxiosResponse<any>) => {
        var data = response.data;
        if (Environment.getBuildType() != "prod") {
          console.log(data);
        }
        transformData(data.ticketList).then((transformedData) => {
          setPurchaseList({ ...data, transformed: [...transformedData] });
        });
      });
    } catch (error) {
      if (Environment.getBuildType() != "prod") {
        console.log(error);
      }
      navigate(`/error?code=${error.response?.status}`);
    }
  };
  /**
   * Transforms the given data by adding additional properties to each ticket.
   *
   * @param {Array} data - The array of ticket data to transform.
   * @returns {Promise<Array>} A promise that resolves with the transformed data array.
   */
  async function transformData(data) {
    const promisedData = data.map(async (ticket) => {
      const reservationDate =
        ticket.reservationTime && getReservationDate(ticket.reservationTime);
      const isExpired = reservationDate ? hasExpired(reservationDate) : null;
      const isMoreThan30 =
        reservationDate && minutesLeftTillSupport(reservationDate);
      const type = ticket.reservationTime
        ? ticket.reservationTime.split("-")[0]
        : "UNSUPPORTED";
      const supportTypeJp =
        ticket.supportType == "TEL"
          ? "電話"
          : ticket.supportType == "ONLINE" && "オンライン";
      return {
        ...ticket,
        isExpired: isExpired,
        type: type,
        isMoreThan30: isMoreThan30,
        supportTypeJp: supportTypeJp,
      };
    });
    return Promise.all(promisedData);
  }
  /**
   * Retrieves the reservation date from the given date string and checks if it has expired.
   *
   * @param {string} date - The date string in a specific format.
   * @returns {boolean} A boolean indicating whether the reservation date has expired.
   */
  function getReservationDate(date) {
    var arr = date.split("-");
    var localeDate = dateTimeToUnix(`${arr[1]} ${arr[2]}:${arr[3]}`);
    // return hasExpired(localeDate);
    return localeDate;
  }
  /**
   * Handles the reservation date change for a ticket and navigates to the support staff page.
   *
   * @param {Object} ticket - The ticket object representing the reservation.
   */
  function reservationDateChange(ticket) {
    if (Environment.getBuildType() != "prod") {
      console.log(ticket);
    }
    var minutesToSupport = 0;
    for (const item of ticket.reservationMenuList) {
      minutesToSupport += item.estimatedMinute;
    }
    navigate(
      `/support/reservation-date-change?ticketNumber=${ticket.ticketNumber}`
    );
  }
  /**
   * Handles the change event when selecting a period and updates the purchase list data accordingly.
   *
   * @param {Event} event - The change event object.
   * @returns {void}
   */
  function handleSelectChange(event) {
    let value = event.target.value;
    setPeriod(value);
    if (value == "過去3ヶ月") {
      value = "";
    }

    getPurchaseListData(value);
  }
  /**
   * Loads more purchase list data based on the last evaluated key and updates the purchase list state.
   *
   * @param {Event} e - The event object triggering the load more action.
   * @returns {Promise<void>} A promise that resolves when the additional purchase list data is loaded.
   */
  async function loadMore(e) {
    if (loading) {
      return;
    }
    setLoading(true);
    var prd = period;
    if (period == "過去3ヶ月") {
      prd = "";
    }
    try {
      await httpGet({
        pathName: "purchase-list",
        queryParams: {
          period: prd,
          memberId: purchaseList.lastEvaluatedKey?.memberId,
          ticketNumber: purchaseList.lastEvaluatedKey?.ticketNumber,
          purchaseTime: purchaseList.lastEvaluatedKey?.purchaseTime,
        },
      }).then((response: AxiosResponse<any>) => {
        var data = response.data;
        if (Environment.getBuildType() != "prod") {
          console.log(data);
        }
        transformData(data.ticketList).then((transformedData) => {
          setPurchaseList((oldData) => ({
            ...oldData,
            lastEvaluatedKey: data.lastEvaluatedKey && data.lastEvaluatedKey,
            transformed: [...oldData.transformed, ...transformedData],
            ticketList: [...oldData.ticketList, ...data.ticketList],
          }));
        });
      });
    } catch (error) {
      if (Environment.getBuildType() != "prod") {
        console.log(error);
      }
      navigate(`/error?code=${error.response?.status}`);
    } finally {
      setLoading(false);
    }
  }
  const date = new Date();
  const thisYear = date.getFullYear();
  const yearsList: number[] = [];
  for (let i: number = 2023; i <= thisYear; i++) {
    yearsList.push(i);
  }
  return (
    <Fragment>
      <Box component={"section"} className="section bg-dots">
        <Container className="container">
          <SectionHeader
            currentPage={"お支払い履歴"}
            preHeading={"Purchase List"}
          />
          {purchaseList ? (
            <Fragment>
              <Container style={{ textAlign: "right", marginBottom: "1.6rem" }}>
                <FormControl variant="outlined">
                  <Select
                    labelId="demo-simple-select-filled-label"
                    id="demo-simple-select-filled"
                    value={period}
                    style={{
                      width: "200px",
                      maxWidth: "98%",
                      textAlign: "left",
                    }}
                    onChange={(e) => {
                      handleSelectChange(e);
                    }}
                  >
                    <MenuItem value={"過去3ヶ月"}>過去3ヶ月</MenuItem>
                    {yearsList.map((year: number, idx: number) => (
                      <MenuItem value={year} key={idx}>
                        {year}年
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Container>

              <Container className="container">
                {purchaseList.transformed.length ? (
                  purchaseList.transformed.map((ticket, idx) => {
                    const status = ticket.supportStatus;
                    return (
                      <Box className="purchase-list-card" key={idx}>
                        <Box className="purchase-list-card-header">
                          <Grid
                            container
                            spacing={1}
                            style={{ justifyContent: "space-between" }}
                          >
                            <Grid item container md sm spacing={2}>
                              <Grid item>
                                <Typography>ご購入日</Typography>
                                <Typography>
                                  {formatDate(ticket.purchaseTime, true, "TZ")}
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography>合計金額</Typography>
                                <Typography>
                                  {ticket.amount
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                  円
                                </Typography>
                              </Grid>
                              {ticket.couponCode && (
                                <Grid item>
                                  <Typography>ご利用クーポンコード</Typography>
                                  <Typography>{ticket.couponCode}</Typography>
                                </Grid>
                              )}
                            </Grid>
                            <Grid item sm={"auto"}>
                              <Typography>注文番号</Typography>
                              <Typography>{ticket.receiptNumber}</Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Box className="purchase-list-card-content">
                          <Grid
                            container
                            spacing={2}
                            alignItems={
                              ["TIP", "INTERVIEW"].includes(ticket.productType)
                                ? "center"
                                : "stretch"
                            }
                          >
                            {ticket.productType == "TIP" && (
                              <Grid
                                item
                                md={2}
                                sm={12}
                                xs={12}
                                container
                                spacing={2}
                              >
                                <img
                                  src={`/images/tip-${ticket.amountBeforeCoupon}.png`}
                                />
                              </Grid>
                            )}
                            {ticket.productType == "INTERVIEW" && (
                              <Grid
                                item
                                md={2}
                                sm={12}
                                xs={12}
                                container
                                spacing={2}
                              >
                                <img src={"/images/interview-image.png"} />
                              </Grid>
                            )}
                            {["SUPPORT"].includes(ticket.productType) && (
                              <Grid
                                item
                                md={2}
                                sm={12}
                                xs={12}
                                container
                                spacing={2}
                              >
                                <Grid item sm={"auto"} md={12}>
                                  <img
                                    src={
                                      ticket.staffImageUrl
                                        ? ticket.staffImageUrl
                                        : "/images/staff_noimage.png"
                                    }
                                  />
                                </Grid>

                                <Grid item sm md={12}>
                                  <Typography>
                                    {ticket.staffNickname}
                                  </Typography>
                                  <Typography>
                                    {ticket.staffAge
                                      ? ticket.staffAge + ", "
                                      : ""}
                                    {ticket.staffGender && (
                                      <Fragment>
                                        {ticket.staffGender == "MALE"
                                          ? "男性"
                                          : "女性"}
                                      </Fragment>
                                    )}
                                  </Typography>
                                  {ticket.supportType && (
                                    <Typography className="support-type">
                                      サポート方法
                                      <span>{ticket.supportTypeJp}</span>
                                    </Typography>
                                  )}
                                </Grid>
                              </Grid>
                            )}

                            <Grid item md sm xs>
                              {ticket.productType == "SUPPORT" ? (
                                <Typography
                                  component={"h3"}
                                  variant={"h5"}
                                  className="ticket-name"
                                >
                                  {ticket.amountRefunded &&
                                  ticket.amountRefunded > 0 ? (
                                    "返金しました"
                                  ) : (
                                    <Fragment>
                                      {ticket.supportStatus == "UNSOLVED" &&
                                        "サポートを完了しました"}
                                      {ticket.supportStatus == "UNSUPPORTED" &&
                                        ticket.isExpired == false &&
                                        "サポートを予約しました"}
                                      {ticket.supportStatus == "COMPLETED" &&
                                        "サポートを完了しました"}
                                      {ticket.supportStatus == "UNSUPPORTED" &&
                                        ticket.isExpired &&
                                        "ご連絡がありませんでした"}
                                      {ticket.supportStatus ==
                                        "UNSOLVED_COMPLETED" &&
                                        ticket.unsolvedAfterFollowContent.includes(
                                          "返金"
                                        ) &&
                                        "返金しました"}
                                      {ticket.supportStatus ==
                                        "UNSOLVED_COMPLETED" &&
                                        !ticket.unsolvedAfterFollowContent.includes(
                                          "返金"
                                        ) &&
                                        "サポートを完了しました"}
                                      {ticket.supportStatus == "UNSOLVED" &&
                                        ticket.refundReason != "" &&
                                        "サポートを完了しました"}
                                    </Fragment>
                                  )}
                                </Typography>
                              ) : (
                                <Typography
                                  component={"h3"}
                                  variant={"h5"}
                                  className="ticket-name"
                                >
                                  {ticket.amountRefunded &&
                                  ticket.amountRefunded > 0
                                    ? "返金しました"
                                    : ""}
                                  {ticket.supportStatus == "COMPLETED"
                                    ? "問診を完了しました"
                                    : ""}
                                </Typography>
                              )}
                              <Typography
                                component={"h3"}
                                variant={"h5"}
                                className="ticket-number"
                              >
                                {ticket.productName} ({ticket.ticketNumber})
                              </Typography>
                              {ticket.reservationMenuList.map((detail, id) => (
                                <Typography className="ticket-details" key={id}>
                                  (目安時間 最大{detail.estimatedMinute}
                                  分)
                                </Typography>
                              ))}
                            </Grid>
                            <Grid
                              md={3}
                              sm={3}
                              xs={12}
                              item
                              container
                              direction={"column"}
                              alignItems={"flex-start"}
                            >
                              {ticket.reservationTime && (
                                <Fragment>
                                  <Typography
                                    className="ticket-r-date"
                                    component={"h4"}
                                    variant={"h6"}
                                  >
                                    ご予約日時
                                  </Typography>
                                  <Typography
                                    className="ticket-r-date"
                                    component={"h4"}
                                    variant={"h6"}
                                  >
                                    {printDateTime(ticket.reservationTime)}
                                  </Typography>
                                </Fragment>
                              )}
                              <Box className="purchase-list-btns-wrapper">
                                {status == "UNSUPPORTED" &&
                                  ticket.reservationTime?.split("-")[0] ==
                                    "SUPPORT" &&
                                  !ticket.isExpired &&
                                  ticket.isMoreThan30 &&
                                  (ticket.amountRefunded <= 0 ||
                                    ticket.amountRefunded == undefined) && (
                                    <Button
                                      className="purchase-list-btn"
                                      color="secondary"
                                      onClick={() =>
                                        reservationDateChange(ticket)
                                      }
                                    >
                                      予約日時の変更
                                    </Button>
                                  )}
                                {status == "COMPLETED" &&
                                  ticket.type == "SUPPORT" &&
                                  (ticket.amountRefunded <= 0 ||
                                    ticket.amountRefunded == undefined) && (
                                    <Button
                                      className="purchase-list-rating-btn"
                                      variant="contained"
                                      color={"primary"}
                                      disabled={ticket.alreadyRating}
                                      onClick={(e) =>
                                        navigate({
                                          pathname: "/rating",
                                          search: createSearchParams({
                                            ticketNumber: ticket.ticketNumber,
                                          }).toString(),
                                        })
                                      }
                                    >
                                      {ticket.alreadyRating ? "評価済" : "評価"}
                                    </Button>
                                  )}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography className="text-center">
                    お支払い履歴はございません。
                  </Typography>
                )}
              </Container>

              {purchaseList.lastEvaluatedKey && (
                <Box className="text-center" style={{ paddingBottom: "50px" }}>
                  {loading && <div className="button-overlay"></div>}
                  <Button
                    color="secondary"
                    onClick={(e) => loadMore(e)}
                    style={{
                      borderBottom: !loading ? "1px solid" : "0",
                      borderRadius: "0",
                    }}
                    disabled={loading}
                  >
                    {loading ? <ButtonLoader /> : "さらに読み込む"}
                  </Button>
                </Box>
              )}
            </Fragment>
          ) : (
            <LoaderComponent />
          )}
        </Container>
      </Box>
    </Fragment>
  );
}
